/**
 * terrain_edits.ts — the Edit Mode's terrain layer, as STAMPS (the same tier
 * the settlement pads and building foundations use — never a heightmap).
 *
 * · raise   : additive smooth dome, Δh = value · falloff(d/r)
 * · flatten : blend toward a target height, h += (value − h) · falloff
 * · paint   : override the per-vertex ground-layer weights toward one layer
 *             (commons: the 6 biome grounds; personal: regolith/moss/dirt)
 *
 * Strokes compose IN ORDER (editId), so later brushwork lands on earlier.
 * The registry lives on globalThis (the module-instance-split law) and the
 * chunk WORKER receives the relevant stamps in its job payload exactly like
 * building stamps. Pure: no DOM, no conn — node-verifiable.
 */

export interface TerrainEditStamp {
  id: number;
  kind: 'raise' | 'flatten' | 'paint';
  x: number;
  z: number;
  r: number;
  v: number;
  layer: number;
}

const G = globalThis as unknown as { __atlasTEdits?: TerrainEditStamp[]; __atlasTEditsVer?: number };
function store(): TerrainEditStamp[] { return (G.__atlasTEdits ??= []); }

/** Replace the whole set (main thread on sync; worker per job). Bumps the version. */
export function setTerrainEdits(list: TerrainEditStamp[]): void {
  const s = store();
  s.length = 0;
  s.push(...list);
  s.sort((a, b) => a.id - b.id);
  G.__atlasTEditsVer = (G.__atlasTEditsVer ?? 0) + 1;
}

export function terrainEdits(): readonly TerrainEditStamp[] { return store(); }
export function terrainEditVersion(): number { return G.__atlasTEditsVer ?? 0; }

/** Stamps whose disc intersects an AABB (worker payload filter). */
export function terrainEditsNear(minX: number, minZ: number, maxX: number, maxZ: number): TerrainEditStamp[] {
  return store().filter((s) => s.x + s.r >= minX && s.x - s.r <= maxX && s.z + s.r >= minZ && s.z - s.r <= maxZ);
}

/** Smooth dome: 1 at the center → 0 at the rim (smoothstep). */
function falloff(d: number, r: number): number {
  if (d >= r) return 0;
  const t = d / r;
  const s = t * t * (3 - 2 * t);
  return 1 - s;
}

/** Height after the world's sculpt strokes (raise + flatten, in order). */
export function applySculpt(h: number, wx: number, wz: number): number {
  for (const s of store()) {
    if (s.kind === 'paint') continue;
    const dx = wx - s.x, dz = wz - s.z;
    if (dx * dx + dz * dz >= s.r * s.r) continue;
    const f = falloff(Math.hypot(dx, dz), s.r);
    if (s.kind === 'raise') h += s.v * f;
    else h += (s.v - h) * f;
  }
  return h;
}

/** The strongest paint layer at a point and its mask (0..1), or null when unpainted. */
export function paintOverride(wx: number, wz: number, layers: number): { layer: number; mask: number } | null {
  let bestLayer = -1, bestMask = 0;
  const acc = new Float32Array(layers);
  for (const s of store()) {
    if (s.kind !== 'paint' || s.layer >= layers) continue;
    const dx = wx - s.x, dz = wz - s.z;
    if (dx * dx + dz * dz >= s.r * s.r) continue;
    const f = falloff(Math.hypot(dx, dz), s.r) * s.v;
    // Later strokes over-paint: accumulate toward 1 per layer, fade the others.
    for (let l = 0; l < layers; l++) acc[l] = l === s.layer ? acc[l] + (1 - acc[l]) * f : acc[l] * (1 - f);
  }
  for (let l = 0; l < layers; l++) if (acc[l] > bestMask) { bestMask = acc[l]; bestLayer = l; }
  return bestLayer >= 0 && bestMask > 0.01 ? { layer: bestLayer, mask: Math.min(1, bestMask) } : null;
}

/** Blend a baked weight vector toward the painted layer: mix(w, onehot(layer), mask). */
export function paintedWeights(w: number[], wx: number, wz: number): number[] {
  const p = paintOverride(wx, wz, w.length);
  if (!p) return w;
  const out = new Array<number>(w.length);
  for (let l = 0; l < w.length; l++) out[l] = w[l] * (1 - p.mask) + (l === p.layer ? p.mask : 0);
  return out;
}
